import React, { useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { X, ArrowRight, Loader2, AlertTriangle } from "lucide-react";

const fmt = (v) => (Number(v) || 0).toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const MOTIVO_LABEL = {
  participacion_parcial_vsm_ute: "Participación parcial (VSM/UTE)",
  sin_dato_participacion: "Sin dato de participación (se asume 100%)",
  coincide_100pct: "Coincide (participación 100%)",
};
const MOTIVO_COLOR = {
  participacion_parcial_vsm_ute: "bg-amber-100 text-amber-800",
  sin_dato_participacion: "bg-rose-100 text-rose-700",
  coincide_100pct: "bg-emerald-100 text-emerald-700",
};

export const ConciliacionPresentadasModal = ({ onClose }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [showCalc, setShowCalc] = useState(false);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/mercado/conciliacion-presentadas`);
        setData(r.data);
      } catch (e) {
        setErr(e.response?.data?.detail || "No se pudo cargar la conciliación");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" data-testid="conciliacion-presentadas-modal">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200">
          <div>
            <h2 className="text-sm font-bold text-gray-900">Conciliación de "Presentadas": Mercado vs Licitaciones</h2>
            <p className="text-[11px] text-gray-500">Puente registro a registro que acredita la diferencia de importe entre ambas vistas.</p>
          </div>
          <button onClick={onClose} data-testid="conciliacion-close" className="text-gray-400 hover:text-gray-700"><X size={18} /></button>
        </div>

        <div className="p-5 overflow-y-auto">
          {loading && <div className="flex items-center gap-2 text-sm text-gray-500 py-10 justify-center"><Loader2 className="animate-spin" size={16} /> Calculando conciliación…</div>}
          {err && <div className="text-sm text-rose-600 py-6 text-center">{err}</div>}
          {data && (
            <>
              {/* Dos orígenes: Mercado vs dashboard real de Licitaciones */}
              <div className="flex flex-col sm:flex-row items-stretch gap-2 mb-4" data-testid="conciliacion-bridge">
                <div className="flex-1 rounded-xl border border-blue-200 bg-blue-50/50 p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-blue-700 font-bold">Mercado · {data.num_presentadas} ops</p>
                  <p className="text-2xl font-black text-blue-900">{fmt(data.total_mercado_mm)} M€</p>
                  <p className="text-[10px] text-blue-600 mt-1">{data.base_mercado}</p>
                </div>
                <div className="flex items-center justify-center flex-col px-2">
                  <ArrowRight className="text-gray-400" size={22} />
                </div>
                <div className="flex-1 rounded-xl border border-emerald-200 bg-emerald-50/50 p-3 text-center">
                  <p className="text-[10px] uppercase tracking-wide text-emerald-700 font-bold">Módulo Licitaciones (real) · {data.modulo_licitaciones?.num_presentadas} ops</p>
                  <p className="text-2xl font-black text-emerald-900">{fmt(data.modulo_licitaciones?.importe_mm)} M€</p>
                  <p className="text-[10px] text-emerald-600 mt-1">{data.modulo_licitaciones?.fuente}</p>
                </div>
              </div>

              {/* H02 · Cómo se calcula / contrato de métrica de ambas vistas */}
              {data.como_se_calcula && (
                <div className="rounded-xl border border-slate-200 bg-slate-50/60 mb-4" data-testid="conciliacion-como-se-calcula">
                  <button onClick={() => setShowCalc((s) => !s)} data-testid="conciliacion-como-toggle"
                    className="w-full flex items-center justify-between px-3 py-2 text-[11px] font-bold text-slate-700">
                    <span>Cómo se calcula (base económica, IVA, corte, filtros) · corte {data.como_se_calcula.fecha_corte}</span>
                    <span className="text-slate-400">{showCalc ? "▲" : "▼"}</span>
                  </button>
                  {showCalc && (
                    <div className="px-3 pb-3 text-[11px] text-slate-700 space-y-2">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {[["Mercado", data.como_se_calcula.mercado], ["Módulo Licitaciones", data.como_se_calcula.licitaciones]].map(([lbl, m]) => (
                          <div key={lbl} className="rounded-lg bg-white border border-slate-200 p-2">
                            <p className="font-bold text-slate-800 mb-1">{lbl} · {m.operaciones} ops</p>
                            <p><span className="text-slate-400">Fuente:</span> {m.fuente}</p>
                            <p><span className="text-slate-400">Filtro:</span> {m.filtro_efectivo}</p>
                            <p><span className="text-slate-400">Base económica:</span> {m.base_economica}</p>
                            <p><span className="text-slate-400">IVA:</span> {m.iva}</p>
                          </div>
                        ))}
                      </div>
                      <div>
                        <p className="font-semibold text-slate-700">Por qué difieren:</p>
                        <ul className="list-disc pl-4 space-y-0.5">
                          {data.como_se_calcula.por_que_difieren.map((t, i) => <li key={i}>{t}</li>)}
                        </ul>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-1 text-[10px] text-slate-500">
                        <span>Lotes: {data.como_se_calcula.tratamiento.lotes}</span>
                        <span>Desconocidos: {data.como_se_calcula.tratamiento.desconocidos}</span>
                        <span>Redondeo: {data.como_se_calcula.tratamiento.redondeo}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Puente de dos niveles (waterfall) */}
              {data.puente && (
                <div className="rounded-xl border border-gray-200 p-3 mb-4" data-testid="conciliacion-puente">
                  <p className="text-[11px] font-bold text-gray-700 mb-2">Puente acreditado: de Mercado al dashboard real de Licitaciones</p>
                  <div className="space-y-1">
                    {data.puente.map((p, i) => {
                      const isResult = p.paso.trim().startsWith("=");
                      const isNeg = (p.importe_mm || 0) < 0;
                      return (
                        <div key={i} className={`flex items-center justify-between text-[11px] px-2 py-1 rounded ${isResult ? "bg-gray-100 font-bold text-gray-900" : "text-gray-600"}`} data-testid={`puente-paso-${i}`}>
                          <span className="truncate pr-2">{p.paso}{p.ops != null && !isResult ? ` (${p.ops} ops)` : p.ops != null && isResult ? ` · ${p.ops} ops` : ""}</span>
                          <span className={`tabular-nums whitespace-nowrap ${isResult ? "text-gray-900" : isNeg ? "text-rose-600" : "text-emerald-700"}`}>{isNeg ? "" : isResult ? "" : "+"}{fmt(p.importe_mm)} M€</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* E06++ · Puente de CONTEO de operaciones (186 → 61) */}
              {data.puente_conteo && (
                <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3 mb-4" data-testid="conciliacion-puente-conteo">
                  <p className="text-[11px] font-bold text-indigo-800 mb-2">
                    Puente de conteo: {data.conteo?.mercado_ops} operaciones (Mercado) → {data.conteo?.licitaciones_ops} operaciones (Licitaciones)
                  </p>
                  <div className="space-y-1">
                    {data.puente_conteo.map((p, i) => {
                      const isResult = p.paso.trim().startsWith("=");
                      const isNeg = (p.n || 0) < 0;
                      return (
                        <div key={i} className={`flex items-center justify-between text-[11px] px-2 py-1 rounded ${isResult ? "bg-indigo-100 font-bold text-indigo-900" : "text-indigo-700/80"}`} data-testid={`puente-conteo-paso-${i}`}>
                          <span className="truncate pr-2">{p.paso}</span>
                          <span className={`tabular-nums whitespace-nowrap font-semibold ${isResult ? "text-indigo-900" : isNeg ? "text-rose-600" : "text-emerald-700"}`}>{isNeg ? "" : isResult ? "" : "+"}{p.n} ops</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Cobertura */}
              {data.cobertura && (
                <div className="grid grid-cols-3 gap-2 mb-4" data-testid="conciliacion-cobertura">
                  <div className="rounded-lg border border-emerald-200 bg-emerald-50/40 p-2 text-center">
                    <p className="text-[9px] uppercase text-emerald-700 font-bold">Comunes a ambos</p>
                    <p className="text-lg font-black text-emerald-900">{data.cobertura.comunes_n}</p>
                    <p className="text-[9px] text-emerald-600">{fmt(data.cobertura.comunes_licitaciones_mm)} M€ en Licitaciones</p>
                  </div>
                  <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-2 text-center">
                    <p className="text-[9px] uppercase text-blue-700 font-bold">Solo Mercado</p>
                    <p className="text-lg font-black text-blue-900">{data.cobertura.solo_mercado_n}</p>
                    <p className="text-[9px] text-blue-600">{fmt(data.cobertura.solo_mercado_importe_total_mm)} M€ total · no están en Licitaciones</p>
                  </div>
                  <div className="rounded-lg border border-amber-200 bg-amber-50/40 p-2 text-center">
                    <p className="text-[9px] uppercase text-amber-700 font-bold">Solo Licitaciones</p>
                    <p className="text-lg font-black text-amber-900">{data.cobertura.solo_licitaciones_n}</p>
                    <p className="text-[9px] text-amber-600">{fmt(data.cobertura.solo_licitaciones_importe_mm)} M€</p>
                  </div>
                </div>
              )}

              {/* Desglose de la diferencia de base (nivel 1) */}
              <div className="rounded-xl border border-gray-200 p-3 mb-4">
                <p className="text-[11px] font-bold text-gray-700 mb-2">Nivel 1 · base de importe dentro de Mercado (total → participación VSM/UTE): −{fmt(data.diferencia_mm)} M€</p>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(data.desglose_diferencia).map(([k, v]) => (
                    <span key={k} className={`text-[11px] px-2 py-1 rounded-full font-semibold ${MOTIVO_COLOR[k]}`} data-testid={`conciliacion-motivo-${k}`}>
                      {MOTIVO_LABEL[k] || k}: {fmt(v)} M€
                    </span>
                  ))}
                </div>
                {data.registros_sin_dato_participacion > 0 && (
                  <p className="text-[10px] text-rose-600 mt-2 flex items-center gap-1">
                    <AlertTriangle size={11} /> {data.registros_sin_dato_participacion} expediente(s) sin dato de participación VSM/UTE: se asumen al 100% y conviene revisarlos.
                  </p>
                )}
              </div>

              {/* Tabla registro a registro */}
              <div className="overflow-x-auto border border-gray-200 rounded-xl">
                <table className="w-full text-[11px]">
                  <thead className="bg-gray-50 text-gray-500 uppercase text-[9px]">
                    <tr>
                      <th className="px-2 py-2 text-left">Cliente</th>
                      <th className="px-2 py-2 text-left">Expediente</th>
                      <th className="px-2 py-2 text-center">En Licit.</th>
                      <th className="px-2 py-2 text-right">Total (M€)</th>
                      <th className="px-2 py-2 text-right">Participación (M€)</th>
                      <th className="px-2 py-2 text-right">% part.</th>
                      <th className="px-2 py-2 text-right">Δ (M€)</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100" data-testid="conciliacion-table">
                    {data.records.map((r) => (
                      <tr key={r.id} className="hover:bg-gray-50">
                        <td className="px-2 py-1.5 font-medium text-gray-800 max-w-[180px] truncate" title={r.cliente}>{r.cliente}</td>
                        <td className="px-2 py-1.5 text-gray-500 max-w-[140px] truncate" title={r.expediente}>{r.expediente}</td>
                        <td className="px-2 py-1.5 text-center">{r.en_licitaciones ? <span className="text-emerald-600 font-bold">✓</span> : <span className="text-gray-300">—</span>}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{fmt(r.importe_total_mm)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums text-emerald-700">{fmt(r.importe_participacion_mm)}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{r.participacion_pct != null ? `${r.participacion_pct}%` : "—"}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums font-semibold text-amber-700">{fmt(r.delta_mm)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-gray-400 mt-2">{data.nota}</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ConciliacionPresentadasModal;
